import { useEffect, useCallback } from 'react';

export interface UseCanvasKeyboardOptions {
  /** Currently selected node ID (from ReactFlow selection). */
  selectedNodeId?: string | null;
  /** Set of currently expanded group IDs. */
  expandedGroups: Set<string>;
  /** Toggle a single group's expanded state. */
  onToggleGroup: (groupId: string) => void;
  /** Replace the entire expandedGroups set. */
  onSetExpandedGroups: (groups: Set<string>) => void;
  /** All known group IDs (for expand-all / collapse-all). */
  allGroupIds: string[];
  /** Called when Delete is pressed on a selected mapping node. */
  onDeleteSelected?: () => void;
  /** Called when Escape is pressed — deselect / close panels. */
  onEscape?: () => void;
}

/**
 * Phase 1 (P0): Keyboard shortcuts for canvas group expand/collapse.
 *
 * - `E`         — Toggle expand/collapse on the selected group
 * - `Shift+E`   — Expand all groups
 * - `Shift+C`   — Collapse all groups
 * - `Delete`     — Delete selected mapping
 * - `Escape`     — Deselect / close panels
 */
export function useCanvasKeyboard({
  selectedNodeId,
  expandedGroups,
  onToggleGroup,
  onSetExpandedGroups,
  allGroupIds,
  onDeleteSelected,
  onEscape,
}: UseCanvasKeyboardOptions) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't intercept when user is typing in an input/textarea/contenteditable
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      // Shift+E → Expand all groups
      if (key === 'e' && event.shiftKey) {
        event.preventDefault();
        onSetExpandedGroups(new Set(allGroupIds));
        return;
      }

      // Shift+C → Collapse all groups
      if (key === 'c' && event.shiftKey) {
        event.preventDefault();
        onSetExpandedGroups(new Set());
        return;
      }

      // E → Toggle selected group
      if (key === 'e' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        if (selectedNodeId && selectedNodeId.startsWith('group:')) {
          event.preventDefault();
          onToggleGroup(selectedNodeId);
        }
        return;
      }

      // Delete → Delete selected mapping
      if ((key === 'delete' || key === 'backspace') && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        if (selectedNodeId && onDeleteSelected) {
          event.preventDefault();
          onDeleteSelected();
        }
        return;
      }

      // Escape → Deselect / close panels
      if (key === 'escape') {
        onEscape?.();
        return;
      }
    },
    [selectedNodeId, onToggleGroup, onSetExpandedGroups, allGroupIds, onDeleteSelected, onEscape]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
